import { useCallback, useEffect, useRef } from "react";
import { captureException } from "@sentry/react";
import { Spinner } from "@aidkitorg/component-library";
import { usePost } from "../../../API";
import { useConnectionModal } from "./ConnectionModalContext";
import { SourceHeader } from "./SourceHeader";
import { type ConnectionModalState } from "./types";
import { useMarkdown } from "../../../Util";

const POLL_INTERVAL = 500;

interface ConnectionModalPendingPageProps {
  state: ConnectionModalState & { stage: "pending" };
}

export function ConnectionModalPendingPage({
  state,
}: ConnectionModalPendingPageProps) {
  const getWebhookResponse = usePost("/deck/get_webhook_response");
  const getListOfAccounts = usePost("/deck/get_list_of_accounts");
  const { setAccessToken, getLocalizedString, onUpdateState, onSelectAccount } = useConnectionModal();
  const pollPromise = useRef<Promise<void>>();
  
  const loadingText = useMarkdown(getLocalizedString("loadingText", "loading_text"), undefined, true);

  const checkForWebhook = useCallback(async () => {
    const response = await getWebhookResponse({ job_guid: state.jobGuid });

    if (!response.response) {
      // Keep polling
      return;
    }

    switch (response.body.webhook_code) {
      case "EnsureConnection":
        const accessToken = response.body.output.access_token;  
        setAccessToken(accessToken);
        const jobGuid = await getListOfAccounts({ access_token: accessToken });
        return onUpdateState({
          stage: "pending",
          selectedSource: state.selectedSource,
          jobGuid,
        });
      case "MfaRequired":
        return onUpdateState({
          stage: "additional_question",
          selectedSource: state.selectedSource,
          jobGuid: state.jobGuid,
          question: response.body.question,
        });
      case "FetchAccounts_ak":
        if (response.body.output.success && response.body.output.account_ids.length === 1) {
          return onSelectAccount(response.body.output.account_ids[0]);
        } else if (response.body.output.success) {
          return onUpdateState({
            stage: "select_account",
            selectedSource: state.selectedSource,
            accountIds: response.body.output.account_ids,
          });
        } else {
          return onUpdateState({
            stage: "error",
            selectedSource: state.selectedSource,
            errorMessage: response.body.output.message,
          });
        }
      case "Error":
        return onUpdateState({
          stage: "error",
          selectedSource: state.selectedSource,
          errorMessage: response.body.error.errorMessage,
        });
      default:
        // Unknown webhook response
        // Alert us but don't progress the UI in case something is wrong
        captureException(new Error("Received unrecognized Deck webhook"), {
          extra: {
            webhook: response.body.webhook_code,
          },
        });
    }
  }, [state]);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (!pollPromise.current) {
        pollPromise.current = checkForWebhook();
        await pollPromise.current;
        pollPromise.current = undefined;
      }
    }, POLL_INTERVAL);

    return () => {
      clearInterval(timer);
      pollPromise.current = undefined;
    };
  }, [checkForWebhook]);

  return <div>
    <SourceHeader source={state.selectedSource} />
    <Spinner />

    <span>{loadingText}</span>
  </div>;
}
